/**
 * Upload ceiling used when a form configures none, so an unbounded file can never reach storage.
 * Matches the "up to 50 MB" the Figma dropzone advertises; override per field with
 * `props.maxFileSizeMb`.
 */
export const DEFAULT_MAX_FILE_SIZE_MB = 50;

/** Result of {@link validateUploadFiles}: either the accepted files, or a Dutch error message. */
export type UploadValidationResult = { files: File[]; error?: undefined } | { files?: undefined; error: string };

/**
 * Resolve the effective size ceiling in MB. Falls back to `DEFAULT_MAX_FILE_SIZE_MB` when the
 * configured value is missing, non-numeric or non-positive — an unset ceiling must never mean
 * "unbounded".
 */
export function resolveMaxFileSizeMb(configured: number | string | null | undefined): number {
  const parsed = typeof configured === 'number' ? configured : Number(configured);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_MAX_FILE_SIZE_MB;
}

/**
 * Coerce a configured `acceptedTypes` into a clean string array.
 *
 * The form builder stores this prop as an empty string when no types are configured (and admins
 * can type a comma-separated list), so a `?? []` guard is not enough — it only catches null and
 * undefined, and `''.filter` throws. Anything that is neither an array nor a string means
 * "no restriction".
 */
export function normalizeAcceptedTypes(value: unknown): string[] {
  const raw = Array.isArray(value) ? value : typeof value === 'string' ? value.split(',') : [];
  return raw
    .filter((type): type is string => typeof type === 'string')
    .map(type => type.trim())
    .filter(Boolean);
}

/**
 * Reject a selection before anything is uploaded, or hand back the accepted files.
 *
 * `UploadDropzoneComponent`'s `accept` input only filters the native picker dialog — it enforces
 * nothing on a drop — so every consumer of its `filesSelected` output MUST call this before
 * uploading. This is a UX guard, not a security boundary: both the extension and `file.type` are
 * supplied by whoever uploads (the browser derives the MIME type from the extension), so a
 * renamed file passes. The server must enforce the same type and size rules on the stored
 * document; this only spares the user a round trip and keeps obvious mistakes out of storage.
 *
 * Shared by `formly-field-upload-document` and the required-documents checklist so both drop
 * targets enforce identical rules.
 */
export function validateUploadFiles(
  fileList: FileList | null | undefined,
  acceptedTypes: string[] | string | null | undefined,
  maxFileSizeMb: number | string | null | undefined
): UploadValidationResult {
  const files = fileList ? Array.from(fileList) : [];
  if (files.length === 0) return { files };

  const rawTypes = normalizeAcceptedTypes(acceptedTypes);
  const allowed = rawTypes.map(type => type.toLowerCase());
  // Mirrors the `accept` attribute format (comma-joined, no space) so the error text matches
  // what the user was shown before picking/dropping.
  const acceptDisplay = rawTypes.join(',');
  const maxMb = resolveMaxFileSizeMb(maxFileSizeMb);

  for (const file of files) {
    if (allowed.length) {
      const name = file.name.toLowerCase();
      const typeOk = allowed.some(type => (type.startsWith('.') ? name.endsWith(type) : file.type === type));
      if (!typeOk) {
        return { error: `Alleen de volgende bestandstypen zijn toegestaan: ${acceptDisplay}.` };
      }
    }
    if (file.size === 0) {
      return { error: `${file.name} is leeg.` };
    }
    if (file.size > maxMb * 1024 * 1024) {
      return { error: `${file.name} is te groot. Het maximum is ${maxMb} MB.` };
    }
  }

  return { files };
}
